import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';

export enum TradeInDevice {
  RING_AIR = 'RING_AIR',
  OURA_GEN_4 = 'OURA_GEN_4',
  SAMSUNG_GALAXY_RING = 'SAMSUNG_GALAXY_RING',
  RING_CONN = 'RING_CONN',
  OURA_GEN_3 = 'OURA_GEN_3',
}

export const TRADE_IN_DEVICE_NAMES: Record<TradeInDevice, string> = {
  [TradeInDevice.RING_AIR]: 'Ring AIR',
  [TradeInDevice.OURA_GEN_4]: 'Oura Gen 4',
  [TradeInDevice.SAMSUNG_GALAXY_RING]: 'Samsung Galaxy Ring',
  [TradeInDevice.RING_CONN]: 'Ring Conn',
  [TradeInDevice.OURA_GEN_3]: 'Oura Gen 3',
};

export const UHX_BONUS: Partial<Record<ShopifyStore, number>> = {
  [ShopifyStore.US]: 30,
  [ShopifyStore.ROW]: 30,
  [ShopifyStore.EU]: 30,
  [ShopifyStore.GB]: 25,
  [ShopifyStore.AE]: 110,
  [ShopifyStore.IN]: 2000,
  [ShopifyStore.CA]: 40,
  [ShopifyStore.AU]: 45,
  [ShopifyStore.SA]: 130,
  [ShopifyStore.ZA]: 575,
  [ShopifyStore.MX]: 625,
  [ShopifyStore.JP]: 5400,
};

const USD_BASE_CREDITS: Record<TradeInDevice, number> = {
  [TradeInDevice.RING_AIR]: 85,
  [TradeInDevice.OURA_GEN_4]: 50,
  [TradeInDevice.SAMSUNG_GALAXY_RING]: 45,
  [TradeInDevice.RING_CONN]: 35,
  [TradeInDevice.OURA_GEN_3]: 25,
};

const TRADE_IN_BASE_CREDITS: Record<
  TradeInDevice,
  Partial<Record<ShopifyStore, number>>
> = {
  [TradeInDevice.RING_AIR]: {
    [ShopifyStore.US]: USD_BASE_CREDITS[TradeInDevice.RING_AIR],
    [ShopifyStore.ROW]: USD_BASE_CREDITS[TradeInDevice.RING_AIR],
    [ShopifyStore.EU]: 85,
    [ShopifyStore.GB]: 65,
    [ShopifyStore.AE]: 310,
    [ShopifyStore.IN]: 7000,
    [ShopifyStore.CA]: 120,
    [ShopifyStore.AU]: 135,
    [ShopifyStore.SA]: 370,
    [ShopifyStore.ZA]: 1650,
    [ShopifyStore.MX]: 1750,
    [ShopifyStore.JP]: 14500,
  },
  [TradeInDevice.OURA_GEN_4]: {
    [ShopifyStore.US]: USD_BASE_CREDITS[TradeInDevice.OURA_GEN_4],
    [ShopifyStore.ROW]: USD_BASE_CREDITS[TradeInDevice.OURA_GEN_4],
    [ShopifyStore.EU]: 50,
    [ShopifyStore.GB]: 40,
    [ShopifyStore.AE]: 185,
    [ShopifyStore.IN]: 5500,
    [ShopifyStore.CA]: 70,
    [ShopifyStore.AU]: 80,
    [ShopifyStore.SA]: 215,
    [ShopifyStore.ZA]: 975,
    [ShopifyStore.MX]: 1025,
    [ShopifyStore.JP]: 8600,
  },
  [TradeInDevice.SAMSUNG_GALAXY_RING]: {
    [ShopifyStore.US]: USD_BASE_CREDITS[TradeInDevice.SAMSUNG_GALAXY_RING],
    [ShopifyStore.ROW]: USD_BASE_CREDITS[TradeInDevice.SAMSUNG_GALAXY_RING],
    [ShopifyStore.EU]: 40,
    [ShopifyStore.GB]: 35,
    [ShopifyStore.AE]: 165,
    [ShopifyStore.IN]: 4000,
    [ShopifyStore.CA]: 65,
    [ShopifyStore.AU]: 70,
    [ShopifyStore.SA]: 195,
    [ShopifyStore.ZA]: 875,
    [ShopifyStore.MX]: 925,
    [ShopifyStore.JP]: 7700,
  },
  [TradeInDevice.RING_CONN]: {
    [ShopifyStore.US]: USD_BASE_CREDITS[TradeInDevice.RING_CONN],
    [ShopifyStore.ROW]: USD_BASE_CREDITS[TradeInDevice.RING_CONN],
    [ShopifyStore.EU]: 35,
    [ShopifyStore.GB]: 30,
    [ShopifyStore.AE]: 130,
    [ShopifyStore.IN]: 3500,
    [ShopifyStore.CA]: 50,
    [ShopifyStore.AU]: 55,
    [ShopifyStore.SA]: 150,
    [ShopifyStore.ZA]: 675,
    [ShopifyStore.MX]: 725,
    [ShopifyStore.JP]: 6000,
  },
  [TradeInDevice.OURA_GEN_3]: {
    [ShopifyStore.US]: USD_BASE_CREDITS[TradeInDevice.OURA_GEN_3],
    [ShopifyStore.ROW]: USD_BASE_CREDITS[TradeInDevice.OURA_GEN_3],
    [ShopifyStore.EU]: 25,
    [ShopifyStore.GB]: 20,
    [ShopifyStore.AE]: 100,
    [ShopifyStore.IN]: 3000,
    [ShopifyStore.CA]: 35,
    [ShopifyStore.AU]: 40,
    [ShopifyStore.SA]: 110,
    [ShopifyStore.ZA]: 475,
    [ShopifyStore.MX]: 500,
    [ShopifyStore.JP]: 4300,
  },
};

export const TRADE_IN_SUPPORTED_STORES = new Set<ShopifyStore>([
  ShopifyStore.US,
  ShopifyStore.EU,
  ShopifyStore.GB,
  ShopifyStore.AE,
  ShopifyStore.IN,
  ShopifyStore.ROW,
  ShopifyStore.CA,
  ShopifyStore.AU,
  ShopifyStore.SA,
  ShopifyStore.ZA,
  ShopifyStore.MX,
  ShopifyStore.JP,
]);

/**
 * Affiliate codes whose buy-page redirect must not offer trade-in.
 * Matched case-insensitively against `?affiliateCode=`.
 * Add a code here to hide trade-in on that partner's redirect only.
 */
const TRADE_IN_DISABLED_AFFILIATE_CODES = new Set<string>([
  'companionclub50',
]);

export const TRADE_IN_STORE_CURRENCY: Partial<Record<ShopifyStore, string>> = {
  [ShopifyStore.US]: 'USD',
  [ShopifyStore.ROW]: 'USD',
  [ShopifyStore.EU]: 'EUR',
  [ShopifyStore.GB]: 'GBP',
  [ShopifyStore.AE]: 'AED',
  [ShopifyStore.IN]: 'INR',
  [ShopifyStore.CA]: 'CAD',
  [ShopifyStore.AU]: 'AUD',
  [ShopifyStore.SA]: 'SAR',
  [ShopifyStore.ZA]: 'ZAR',
  [ShopifyStore.MX]: 'MXN',
  [ShopifyStore.JP]: 'JPY',
};

export function getTradeInCredit(
  device: TradeInDevice,
  store: ShopifyStore,
  hasUhx: boolean,
): number {
  const base = TRADE_IN_BASE_CREDITS[device]?.[store];
  if (base === undefined) return 0;
  return hasUhx ? base + (UHX_BONUS[store] ?? 0) : base;
}

export function getMaxTradeInCredit(
  store: ShopifyStore,
  hasUhx: boolean,
): number {
  let max = 0;
  for (const device of Object.values(TradeInDevice)) {
    const credit = getTradeInCredit(device, store, hasUhx);
    if (credit > max) max = credit;
  }
  return max;
}

export function isTradeInAvailable(
  store: ShopifyStore | null,
  affiliateCode?: string | null,
): boolean {
  if (!store || !TRADE_IN_SUPPORTED_STORES.has(store)) return false;
  if (
    affiliateCode &&
    TRADE_IN_DISABLED_AFFILIATE_CODES.has(affiliateCode.toLowerCase())
  ) {
    return false;
  }
  return true;
}
